import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Store, Star, Search } from "lucide-react";
import { useBoutiques } from "../hooks/useBoutiques";
import { PageStatus } from "../componnents/UI";
import BoutiqueBadges from "../componnents/BoutiqueBadges/BoutiqueBadges";
import "./CSS/Boutiques.css";

export default function Boutiques() {
  const { t } = useTranslation("common");
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const searchQuery = searchParams.get("search") || "";
  const [searchInput, setSearchInput] = useState(searchQuery);

  const { data, isLoading, error, refetch } = useBoutiques({
    page,
    limit: 20,
    ...(searchQuery.trim() && { search: searchQuery.trim() }),
  });

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    setSearchParams({ page: "1", ...(q && { search: q }) });
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ page: String(newPage), ...(searchQuery && { search: searchQuery }) });
  };

  const boutiques = data?.boutiques ?? [];
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;
  const emptySearch = Boolean(searchQuery.trim());
  const emptyTitle = emptySearch ? t("noBoutiquesSearch") : t("noBoutiquesYet");
  const emptyDescription = emptySearch
    ? t("noBoutiquesSearchDescription")
    : t("noBoutiquesYetDescription");

  return (
    <div className="boutiques-page">
      <div className="boutiques-container">
        <h1 className="boutiques-title">{t("boutiques")}</h1>

        <form className="boutiques-search" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder={t("searchByName")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="boutiques-search-input"
          />
          <button type="submit" className="boutiques-search-btn" aria-label="Search">
            <Search size={20} />
          </button>
        </form>

        <PageStatus
          isLoading={isLoading}
          loadingText={t("loading")}
          error={error}
          onRetry={() => refetch()}
          retryText={t("errors.tryAgain")}
          isEmpty={!isLoading && !error && boutiques.length === 0}
          emptyIcon="store"
          emptyTitle={emptyTitle}
          emptyDescription={emptyDescription}
          emptyActionText={emptySearch ? t("boutiques") : t("empty.exploreShop")}
          emptyActionLink={emptySearch ? "/boutiques" : "/search"}
          variant="section"
        >
          <>
            <div className="boutiques-grid">
              {boutiques.map((b) => (
                <Link key={b.id} to={`/boutiques/${b.id}`} className="boutique-card">
                  <div className="boutique-card-logo">
                    {b.logo ? (
                      <img src={b.logo} alt={b.name} />
                    ) : (
                      <Store size={48} className="placeholder-icon" />
                    )}
                  </div>
                  <div className="boutique-card-body">
                    <h2 className="boutique-card-name">{b.name}</h2>
                    <BoutiqueBadges boutique={b} />
                    <div className="boutique-card-meta">
                      <span className="rating">
                        <Star size={14} fill="currentColor" />
                        {Number(b.rating ?? 0).toFixed(1)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="boutiques-pagination">
                <button
                  type="button"
                  className="pagination-btn"
                  disabled={page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                >
                  {t("previous")}
                </button>
                <span className="pagination-info">
                  {t("page")} {page} / {totalPages} ({total} {t("total")})
                </span>
                <button
                  type="button"
                  className="pagination-btn"
                  disabled={page >= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                >
                  {t("next")}
                </button>
              </div>
            )}
          </>
        </PageStatus>
      </div>
    </div>
  );
}
